import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { offerSaveToPhone, uploadCapturedPhotos } from "@/lib/photoUpload";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import EditTemplatesDialog from "@/components/EditTemplatesDialog";
import PhotoReviewDialog from "@/components/PhotoReviewDialog";
import PhotoReqCard from "@/components/photos/PhotoReqCard";
import SectionNaButton from "@/components/photos/SectionNaButton";
import SectionDropzone from "@/components/photos/SectionDropzone";
import OrphanPhotosCard from "@/components/photos/OrphanPhotosCard";
import PhotoDestinationDialog from "@/components/photos/PhotoDestinationDialog";
import { Share2, CheckSquare, X as XIcon, MoveRight, ShieldCheck, ShieldX, Clock, Images, Volume2, Lock } from "lucide-react";
import { announceBatch, speak, voiceEnabled, setVoiceEnabled } from "@/lib/speak";
import { toast } from "sonner";

// localStorage key for the "save to phone" toggle. Stored client-side
// because it's a per-device UX preference, not user data, and the
// value is a single non-sensitive boolean flag ("0" | "1"). The auth
// cookie itself is httpOnly and lives outside localStorage entirely.
const SAVE_TO_PHONE_KEY = "f2_save_to_phone";
// Same rationale — per-device preference for hiding empty photo
// categories. Defaults to OFF so a fresh install shows the full
// checklist the field crew is used to seeing.
const HIDE_UNUSED_KEY = "f2_hide_unused_categories";

// Small stateless chip for the review-status filter row. Tone maps
// to the same color families used on PhotoThumb rings so filter
// selection and thumbnail highlight visually agree at a glance.
function FilterChip({ active, onClick, icon, label, count, tone, testid }) {
  const tones = {
    slate:   { on: "bg-slate-700 text-slate-50 border-slate-500", off: "border-slate-700 text-slate-300 hover:bg-slate-800" },
    amber:   { on: "bg-amber-500 text-slate-900 border-amber-300", off: "border-amber-800/60 text-amber-300 hover:bg-amber-950/40" },
    red:     { on: "bg-red-600 text-white border-red-400", off: "border-red-800/60 text-red-300 hover:bg-red-950/40" },
    emerald: { on: "bg-emerald-600 text-white border-emerald-400", off: "border-emerald-800/60 text-emerald-300 hover:bg-emerald-950/40" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active ? "true" : "false"}
      className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[11px] font-display font-bold uppercase tracking-wider transition-colors ${active ? t.on : `bg-slate-950 ${t.off}`}`}
      data-testid={testid}
    >
      {icon}
      {label}
      <span className={`ml-0.5 font-mono ${active ? "opacity-90" : "opacity-70"}`}>{count}</span>
    </button>
  );
}

export default function PhotosPanel({ project, onChanged, onGoToJsa }) {
  const { user } = useAuth();
  const canReview = user && ["admin", "pm"].includes(user.role);
  // Field crews can't upload photos until the JSA has a task line + signature.
  const uploadLocked = !canReview && !project.jsa_complete;
  const allowVideo = project.project_type === "pano_site_audit";
  const [voiceOn, setVoiceOn] = useState(voiceEnabled);

  // Spoken reminder each time a locked crew opens the Photos tab.
  useEffect(() => {
    if (uploadLocked) speak("Complete the J S A first. Photo uploads are locked until the J S A is signed.");
  }, [uploadLocked, project.id]);
  const [template, setTemplate] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveToPhone, setSaveToPhone] = useState(
    () => localStorage.getItem(SAVE_TO_PHONE_KEY) === "1"
  );
  // Hide categories with no photos AND no N/A'd items. Mirrors the
  // empty-section-skipping behavior in the generated PDF so the field
  // UI matches the customer deliverable. Persisted per-device.
  const [hideUnused, setHideUnused] = useState(
    () => localStorage.getItem(HIDE_UNUSED_KEY) === "1"
  );
  // Lift the review-dialog state to the panel so any thumbnail can
  // surface it without each PhotoReqCard rendering its own dialog —
  // keeps the DOM tree light when a project has 100+ photos.
  const [reviewing, setReviewing] = useState(null);

  // Bulk-select mode (admin/pm only). When active, thumbnails toggle
  // selection instead of opening the review dialog, and a sticky
  // action bar surfaces the destination picker for a batch move.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  // Client-side filter over the photo grid. "all" (default) shows
  // everything; "pending" and "rejected" restrict to those statuses so
  // a PM triaging review can jump straight to what needs action
  // without scrolling. Kept per-session (no localStorage) so the
  // default view is always "all" when a project is reopened.
  const [reviewFilter, setReviewFilter] = useState("all");

  useEffect(() => {
    api.get(`/templates/photos/${project.project_type}`).then((r) => setTemplate(r.data.template));
  }, [project.project_type]);

  useEffect(() => {
    localStorage.setItem(SAVE_TO_PHONE_KEY, saveToPhone ? "1" : "0");
  }, [saveToPhone]);

  useEffect(() => {
    localStorage.setItem(HIDE_UNUSED_KEY, hideUnused ? "1" : "0");
  }, [hideUnused]);

  const progress = project.photo_progress;
  const photosByKey = {};
  const rs = project.review_summary || {};
  // Bucket photos by their requirement_key AND apply the review-status
  // filter in the same pass. When the filter is not "all", we drop
  // photos whose (normalized) status doesn't match — the PhotoReqCard
  // gets a shorter list, and empty items just show 0 photos.
  const _passesFilter = (p) => {
    if (reviewFilter === "all") return true;
    const s = p.review_status || "pending";
    return s === reviewFilter;
  };
  (project.photos || []).forEach((p) => {
    if (!_passesFilter(p)) return;
    const k = p.requirement_key || "_uncat";
    (photosByKey[k] = photosByKey[k] || []).push(p);
  });

  const handleUpload = async (fileList, reqInfo) => {
    // Snapshot the FileList into a real Array IMMEDIATELY, before any
    // await. The original FileList is live and gets emptied as soon as
    // we reset `input.value` in the onChange handler.
    const files = Array.from(fileList || []);
    if (!reqInfo || !files.length) return;
    if (uploadLocked) {
      toast.error("Complete the JSA before uploading photos.");
      speak("Complete the J S A before uploading photos.");
      return;
    }
    setActiveKey(reqInfo.key);
    setBusy(true);

    // Kick off "save to phone" before the network call (preserves user
    // gesture so the iOS share sheet actually opens).
    if (saveToPhone && files[0]) {
      await offerSaveToPhone(files[0], reqInfo.label);
    }

    try {
      const { uploaded, queued, failed, firstError } = await uploadCapturedPhotos({
        projectId: project.id,
        files,
        reqInfo,
      });
      announceBatch({ uploaded, queued, failed });
      if (failed > 0) {
        toast.error(`${failed} photo${failed === 1 ? "" : "s"} failed: ${firstError}`);
      }
      if (uploaded === 0 && queued === 0) {
        // nothing succeeded — error toast above covers it
      } else if (uploaded > 0 && queued === 0) {
        toast.success(`${uploaded} photo${uploaded === 1 ? "" : "s"} uploaded`);
      } else if (queued > 0 && uploaded === 0) {
        // Queued covers both true-offline AND storage-throttle cases.
        // The sync worker will retry both automatically, so a single
        // message is accurate for the crew either way.
        toast.info(
          `${queued} photo${queued === 1 ? "" : "s"} queued — will finish uploading automatically`
        );
      } else {
        toast.success(`${uploaded} uploaded, ${queued} queued`);
      }
      onChanged?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      console.error("Photo upload error:", err);
      toast.error(`Upload failed: ${msg}`);
    } finally {
      setBusy(false);
      setActiveKey(null);
    }
  };

  // Section-level drop: a batch of images is auto-routed across the
  // section's slots. Fill empty requirements in display order (one
  // per slot), then spill any overflow onto the first non-N/A slot
  // as extras. This saves a desktop user from clicking "Add Photo"
  // on every individual slot when they already have the batch sorted
  // on their disk.
  //
  // Each requirement that gets files is sent through `handleUpload`
  // one-by-one and awaited in sequence so the sync queue sees the
  // right ordering and `setBusy`/`setActiveKey` stay coherent per
  // slot (progress spinner lands on the right card).
  const handleSectionDrop = async (cat, files) => {
    if (!cat || cat.na || !files?.length) return;
    // Build the slot plan from scratch each drop — `cat.items` is
    // already in template order and reflects the current done state
    // from the backend, so this plan is always fresh.
    const openSlots = cat.items.filter((it) => !it.done && !it.na);
    const fallback = cat.items.find((it) => !it.na) || cat.items[0];
    if (!fallback) {
      toast.error("No slots in this section accept uploads.");
      return;
    }
    const assignments = new Map(); // key -> {reqInfo, files[]}
    const assign = (it, f) => {
      const prev = assignments.get(it.key);
      if (prev) {
        prev.files.push(f);
      } else {
        assignments.set(it.key, {
          reqInfo: { category: cat.name, key: it.key, label: it.label },
          files: [f],
        });
      }
    };
    let used = 0;
    files.forEach((f, i) => {
      if (i < openSlots.length) {
        assign(openSlots[i], f);
        used += 1;
      } else {
        // Overflow — pile onto the fallback slot as extras.
        assign(fallback, f);
      }
    });
    const overflow = files.length - used;
    const slotCount = assignments.size;
    // One leading toast so the crew knows what's being routed. Each
    // handleUpload also fires its own per-slot success toast, which
    // is useful on slow links so they can see progress.
    if (overflow > 0 && used > 0) {
      toast.info(
        `Routing ${files.length} photos: ${used} to open slots, ${overflow} extra on "${fallback.label}"`
      );
    } else if (overflow > 0) {
      toast.info(`All slots full — routing ${overflow} as extras on "${fallback.label}"`);
    } else {
      toast.info(`Routing ${files.length} photo${files.length === 1 ? "" : "s"} across ${slotCount} slot${slotCount === 1 ? "" : "s"}`);
    }
    // Sequential awaits keep the busy spinner on the right slot and
    // avoid stampeding the storage layer (now rate-limit-tolerant,
    // but still polite). This is a desktop-only path so adding ~250ms
    // of serialization per file is well below perception threshold.
    for (const { reqInfo, files: fs } of assignments.values()) {
      // eslint-disable-next-line no-await-in-loop
      await handleUpload(fs, reqInfo);
    }
  };

  // Drag a thumbnail onto another slot = move the original file (full quality, no re-upload).
  const movePhoto = async (photoId, target) => {
    const p = (project.photos || []).find((x) => x.id === photoId);
    if (!p || (p.category === target.category && p.requirement_key === target.key)) return;
    if (!canReview) {
      toast.info("Only admins and PMs can move photos. Ask your PM, or retake it in the right slot.");
      return;
    }
    try {
      await api.post(`/projects/${project.id}/photos/recategorize`, {
        photo_ids: [photoId],
        category: target.category,
        requirement_key: target.key,
      });
      toast.success(`Moved photo → ${target.label}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
    }
  };


  const removePhoto = async (id) => {
    // Native confirm — matches the N/A flow's `window.prompt` pattern
    // already used in this panel, no extra dep, works offline. One
    // wrong tap on a tiny mobile thumbnail used to nuke a photo
    // immediately; this gives the user a safety net.
    const ok = window.confirm(
      "Delete this photo?\n\nThis cannot be undone — the photo will no longer appear in the closeout package."
    );
    if (!ok) return;
    try {
      await api.delete(`/files/${id}`);
      toast.success("Photo deleted");
      onChanged?.();
    } catch (err) {
      // The backend blocks deletion of approved photos unless the
      // caller is admin/pm. Surface that gate cleanly instead of a
      // generic toast.
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  // ----- Bulk-select helpers -----
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const enterSelectionMode = () => {
    setSelectedIds(new Set());
    setSelectionMode(true);
  };
  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  // Union set of every requirement_key currently held by ANY selected
  // photo. Passed to the destination dialog so those slots render as
  // disabled — moving photos to a slot they're already in is a no-op.
  const selectedCurrentKeys = useMemo(() => {
    if (!selectionMode || selectedIds.size === 0) return new Set();
    const keys = new Set();
    (project.photos || []).forEach((p) => {
      if (selectedIds.has(p.id) && p.requirement_key) keys.add(p.requirement_key);
    });
    return keys;
  }, [selectionMode, selectedIds, project.photos]);

  // Optionally hide categories that have zero photos AND zero N/A'd
  // items. A category with N/A marks is intentionally exempted — the
  // office side has decided it applies, just with no photos required.
  // In selection mode we always show everything so bulk-move destinations
  // don't disappear mid-workflow.
  const visibleCategories = useMemo(() => {
    const all = progress?.categories || [];
    if (!hideUnused || selectionMode) return all;
    return all.filter((cat) =>
      cat.items.some((it) => it.count > 0 || it.na)
    );
  }, [progress, hideUnused, selectionMode]);
  const hiddenCategoryCount = (progress?.categories?.length || 0) - visibleCategories.length;

  // Bulk-clear the pending review gate. Uses window.confirm on
  // purpose — the destructive-ish message + type-nothing-just-confirm
  // is enough friction for a low-blast-radius action (approving photos
  // that were probably already visually reviewed anyway).
  const bulkApprovePending = async () => {
    const pending = project.review_summary?.pending || 0;
    if (pending === 0) return;
    const ok = window.confirm(
      `Approve all ${pending} pending photo${pending === 1 ? "" : "s"} on this project?\n\n` +
      `Use this when you've already visually reviewed the photos and just want to clear the badge. ` +
      `Rejected photos (retake requests) are NOT changed. You can still reject individual photos later.`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/projects/${project.id}/photos/bulk-approve`, { mode: "pending" });
      toast.success(`${data.approved} photo${data.approved === 1 ? "" : "s"} approved`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk approve failed");
    }
  };

  if (!template) return <div className="text-slate-400">Loading photo template…</div>;

  return (
    <div className="space-y-5" data-testid="photos-panel">
      <OrphanPhotosCard
        project={project}
        progress={progress}
        onChanged={onChanged}
      />

      {uploadLocked && (
        <div
          role="alert"
          className="rounded-lg border border-amber-700 bg-amber-950/40 p-4 flex flex-wrap items-center gap-3"
          data-testid="photos-jsa-lock-banner"
        >
          <Lock className="w-5 h-5 text-amber-300 shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-[200px]">
            <div className="font-display font-bold text-amber-100">Photo uploads are locked until the JSA is complete</div>
            <div className="text-xs text-amber-200/80 mt-0.5">Add at least one task or hazard line and one signature on the JSA tab, then save it.</div>
          </div>
          {onGoToJsa && (
            <Button size="sm" onClick={onGoToJsa} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-bold" data-testid="photos-jsa-lock-go">
              Open JSA
            </Button>
          )}
        </div>
      )}

      {/* Bulk-select toolbar. Sticks to the top of the tab so a PM
          scrolling through 100+ thumbnails can still see the count
          and confirm the batch without scrolling back up. */}
      {canReview && selectionMode && (
        <div
          className="sticky top-16 z-20 -mx-1 px-4 py-2 rounded-md border border-blue-800 bg-blue-950/80 backdrop-blur-sm flex flex-wrap items-center gap-3 shadow-lg"
          data-testid="bulk-select-toolbar"
        >
          <CheckSquare className="w-4 h-4 text-blue-300 shrink-0" />
          <div className="text-sm font-display font-bold text-blue-100" data-testid="bulk-select-count">
            {selectedIds.size} selected
          </div>
          <Button
            size="sm"
            onClick={() => setBulkMoveOpen(true)}
            disabled={selectedIds.size === 0}
            className="bg-red-700 hover:bg-red-600 font-display tracking-wider"
            data-testid="bulk-move-btn"
          >
            <MoveRight className="w-3.5 h-3.5 mr-1.5" />
            MOVE TO…
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedIds(new Set())}
                className="text-blue-200 hover:text-blue-50"
                data-testid="bulk-clear-selection"
              >
                Clear
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={exitSelectionMode}
              className="text-slate-300 hover:text-slate-50"
              data-testid="bulk-exit"
            >
              <XIcon className="w-3.5 h-3.5 mr-1" /> Done
            </Button>
          </div>
        </div>
      )}

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold text-lg">Photo Completion</div>
            <div className="flex items-center gap-3">
              {/* Bulk-clear pending — visible only when there ARE
                  pending photos to clear. Placed next to the primary
                  actions so a PM triaging an older project sees it
                  right where they're already looking. */}
              {canReview && !selectionMode && (project.review_summary?.pending || 0) > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={bulkApprovePending}
                  className="border-emerald-800 bg-emerald-950/40 hover:bg-emerald-900/40 text-emerald-200"
                  data-testid="bulk-approve-pending"
                  title={`Approve all ${project.review_summary.pending} pending photos`}
                >
                  <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                  APPROVE {project.review_summary.pending} PENDING
                </Button>
              )}
              {/* Enter bulk-select mode. Admin/PM only — a foreman
                  moving another crew's photos in bulk isn't a
                  workflow we want to enable. */}
              {canReview && !selectionMode && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={enterSelectionMode}
                  className="border-slate-700 bg-slate-950 hover:bg-slate-800 text-slate-200"
                  data-testid="bulk-select-enter"
                >
                  <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                  Select Photos
                </Button>
              )}
              <EditTemplatesDialog project={project} onChanged={onChanged} />
              <div className="font-mono text-2xl font-bold text-blue-400" data-testid="photo-progress-percent">{progress?.overall_percent ?? 0}%</div>
            </div>
          </div>
          <Progress value={progress?.overall_percent ?? 0} className="h-2 bg-slate-800" />
          <div className="text-xs text-slate-400 mt-2 font-mono">
            {progress?.total_completed ?? 0} / {progress?.total_required ?? 0} required photos captured
          </div>
          <div className="mt-4 pt-3 border-t border-slate-800 flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                <Share2 className="w-3.5 h-3.5" /> Save copies to my phone
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                After each capture, opens the iOS / Android share sheet so you can tap &ldquo;Save Image&rdquo; to your camera roll. Web browsers can&rsquo;t save directly to Photos — this is the closest one-tap workflow.
              </div>
            </div>
            <Switch
              checked={saveToPhone}
              onCheckedChange={setSaveToPhone}
              data-testid="save-to-phone-toggle"
            />
          </div>
          <div className="mt-3 pt-3 border-t border-slate-800 flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                <Volume2 className="w-3.5 h-3.5" /> Spoken upload updates
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                Says out loud when photos finish uploading or are waiting to retry, so you don&rsquo;t have to look at the screen.
              </div>
            </div>
            <Switch
              checked={voiceOn}
              onCheckedChange={(v) => { setVoiceOn(v); setVoiceEnabled(v); if (v) speak("Spoken upload updates on"); }}
              aria-label="Spoken upload updates"
              data-testid="voice-updates-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {progress?.categories?.length > 0 && (
        <div className="flex items-center justify-between px-1" data-testid="hide-unused-row">
          <label className="text-[11px] uppercase tracking-wider text-slate-400 font-display font-bold inline-flex items-center gap-2 cursor-pointer">
            <Switch
              checked={hideUnused}
              onCheckedChange={setHideUnused}
              disabled={selectionMode}
              data-testid="hide-unused-toggle"
            />
            Hide unused categories
          </label>
          {hideUnused && !selectionMode && hiddenCategoryCount > 0 && (
            <button
              type="button"
              onClick={() => setHideUnused(false)}
              className="text-[11px] text-blue-400 hover:text-blue-200 underline underline-offset-2"
              data-testid="hide-unused-show-all"
            >
              {hiddenCategoryCount} more categor{hiddenCategoryCount === 1 ? "y" : "ies"} available — show all
            </button>
          )}
          {selectionMode && hideUnused && (
            <span className="text-[11px] text-slate-500 italic" data-testid="hide-unused-selection-note">
              All categories visible while selecting photos
            </span>
          )}
        </div>
      )}

      {/* Review-status filter chips. Lets a PM jump straight to
          `pending` or `rejected` photos when triaging a big project
          instead of hunting through hundreds of thumbnails. The
          "rejected" chip is always shown when there's ≥1 rejection so
          the field crew's retake list is one tap away, even on
          projects where everything else is approved. Non-reviewers
          still see the chips — a foreman wants to spot their own
          rejected photos just as much. */}
      {progress?.categories?.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 px-1"
          data-testid="review-filter-chips"
          role="tablist"
          aria-label="Filter photos by review status"
        >
          <FilterChip
            active={reviewFilter === "all"}
            onClick={() => setReviewFilter("all")}
            icon={<Images className="w-3 h-3" />}
            label="All"
            count={rs.total || 0}
            tone="slate"
            testid="filter-chip-all"
          />
          <FilterChip
            active={reviewFilter === "pending"}
            onClick={() => setReviewFilter("pending")}
            icon={<Clock className="w-3 h-3" />}
            label="Pending"
            count={rs.pending || 0}
            tone="amber"
            testid="filter-chip-pending"
          />
          <FilterChip
            active={reviewFilter === "rejected"}
            onClick={() => setReviewFilter("rejected")}
            icon={<ShieldX className="w-3 h-3" />}
            label="Rejected"
            count={rs.rejected || 0}
            tone="red"
            testid="filter-chip-rejected"
          />
          <FilterChip
            active={reviewFilter === "approved"}
            onClick={() => setReviewFilter("approved")}
            icon={<ShieldCheck className="w-3 h-3" />}
            label="Approved"
            count={rs.approved || 0}
            tone="emerald"
            testid="filter-chip-approved"
          />
          {reviewFilter !== "all" && (
            <span className="ml-auto text-[11px] text-slate-500 italic" data-testid="filter-chip-hint">
              Showing only {reviewFilter} photos
            </span>
          )}
        </div>
      )}

      {visibleCategories.map((cat) => {
        // Count open slots for the dropzone hint. N/A'd items don't
        // count — they're intentionally excluded from the section.
        const openSlotCount = cat.na
          ? 0
          : cat.items.filter((it) => !it.done && !it.na).length;
        // Section-level drop is disabled when the whole section is
        // N/A'd (uploads blocked server-side anyway) OR when the
        // user is in bulk-select mode (dropping during a move would
        // be ambiguous).
        const sectionDropEnabled = !cat.na && !selectionMode && !uploadLocked;
        return (
        <SectionDropzone
          key={cat.name}
          categoryName={cat.name}
          enabled={sectionDropEnabled}
          slotsAvailable={openSlotCount}
          allowVideo={allowVideo}
          onDropFiles={(files) => handleSectionDrop(cat, files)}
        >
        <Card
          className={`border-slate-800 ${cat.na ? "bg-slate-950/60" : "bg-slate-900"}`}
          data-testid={`photo-cat-${cat.name.replace(/\s+/g, '-').toLowerCase()}`}
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className={`font-display font-bold ${cat.na ? "text-slate-500 line-through" : ""}`}>
                    {cat.name}
                  </div>
                  {cat.na && (
                    <span
                      className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-950/60 border border-amber-800 text-amber-300"
                      data-testid={`cat-na-badge-${cat.name.replace(/\s+/g, '-').toLowerCase()}`}
                      title={cat.na_reason ? `Reason: ${cat.na_reason}` : "Section marked N/A"}
                    >
                      Section N/A{cat.na_by ? ` · by ${cat.na_by}` : ""}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {cat.na
                    ? "Excluded from customer PDF. Uploads disabled."
                    : cat.required === 0
                      ? `Optional — ${cat.items.filter((i) => i.count > 0).length} of ${cat.items.length} taken`
                      : `${cat.completed} / ${cat.required} captured`}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className={`font-mono font-bold ${cat.na ? "text-slate-600" : "text-blue-400"}`}>
                  {cat.required === 0 && !cat.na ? (
                    <span className="text-[10px] uppercase tracking-wider text-sky-300">Optional</span>
                  ) : `${cat.percent}%`}
                </div>
                {/* Section N/A is an admin/PM-only control. Dropping
                    a whole section from the customer deliverable is a
                    management-level call — foremen in the field flag
                    scope changes verbally and the office marks it. */}
                {canReview && (
                  <SectionNaButton
                    projectId={project.id}
                    categoryName={cat.name}
                    isNa={!!cat.na}
                    reason={cat.na_reason}
                    onChanged={onChanged}
                  />
                )}
              </div>
            </div>
            {!cat.na && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {cat.items.map((it) => (
                <PhotoReqCard
                  key={it.key}
                  item={it}
                  categoryName={cat.name}
                  photos={[
                    ...(photosByKey[it.key] || []),
                    ...(it.linked || []).flatMap((lk) => photosByKey[lk.key] || []),
                  ]}
                  busy={busy && activeKey === it.key}
                  projectId={project.id}
                  canReview={canReview}
                  onUpload={handleUpload}
                  onMovePhoto={movePhoto}
                  locked={uploadLocked}
                  allowVideo={allowVideo}
                  onRemove={removePhoto}
                  onNaChanged={onChanged}
                  onPreview={(p) => setReviewing(p)}
                  selectionMode={selectionMode}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </div>
            )}
          </CardContent>
        </Card>
        </SectionDropzone>
        );
      })}

      <PhotoReviewDialog
        projectId={project.id}
        photo={reviewing}
        photoProgress={progress}
        open={!!reviewing}
        onOpenChange={(o) => !o && setReviewing(null)}
        onReviewed={onChanged}
      />

      <PhotoDestinationDialog
        projectId={project.id}
        photoIds={Array.from(selectedIds)}
        progress={progress}
        currentKeys={selectedCurrentKeys}
        title="Move {n} selected photos"
        open={bulkMoveOpen}
        onOpenChange={setBulkMoveOpen}
        onMoved={() => { exitSelectionMode(); onChanged?.(); }}
      />
    </div>
  );
}
